import { palette, petrol, violet, lightSurface, feedback, alpha } from './brand';

/**
 * Tema claro — paleta Hexium. Todos os valores vêm de `brand.ts`; nenhum hex solto aqui.
 *
 * No claro a paleta funciona direto, sem tint: petrol e violet como texto sobre branco dão
 * 10.45:1 e 8.15:1 (AAA). Os tokens `primaryAccent`/`accentText` existem para paridade de
 * interface com o tema escuro e aqui apontam para a cor da marca sem alteração.
 *
 * ⚠️ `grayMedium` (#777777) dá **4.48:1** sobre branco — fica **abaixo** do mínimo AA de 4.5:1.
 * Por isso ele é usado só em `placeholderColor` (conteúdo não-essencial). Texto secundário usa
 * `grayDark` (12.63:1).
 *
 * Este arquivo também define o TIPO do tema (ver `styled.d.ts`), então toda chave usada em
 * componente precisa existir aqui.
 */
export const lightTheme = {
    // Marca — preenchimento
    primary: palette.petrol,          // fundo de botão/gradiente; texto branco por cima = 10.45:1
    primaryHover: petrol.shade15,
    primaryActive: petrol.shade30,
    /** Acento legível sobre superfície clara (10.45:1). */
    primaryAccent: palette.petrol,

    // Marca — secundária (violet)
    accent: palette.violet,           // fundo; texto branco por cima = 8.15:1
    accentHover: violet.shade15,
    /** Violet legível sobre superfície clara (8.15:1). */
    accentText: palette.violet,

    // Superfícies
    secondary: lightSurface.base,
    background: lightSurface.base,
    backgroundAlt: petrol.tint88,
    backgroundSecondary: palette.white,
    card: lightSurface.raised,
    cardBorder: lightSurface.border,
    border: lightSurface.border,
    borderLight: lightSurface.borderStrong,

    // Texto
    text: palette.black,                    // 18.88:1 sobre branco
    textSecondary: palette.grayDark,        // 12.63:1
    title: palette.black,
    label: palette.grayDark,
    placeholderColor: palette.grayMedium,   // 4.48:1 — só placeholder, ver aviso acima
    detailLabelColor: palette.grayDark,
    detailValueColor: palette.black,
    textSubtitleCompliance: palette.grayDark,
    ruleDetailsTitleColor: palette.black,
    arrowBack: palette.black,
    inputAddEditUser: palette.black,
    inputAddEditOrganization: palette.black,
    inputAddEditRule: palette.black,
    buttonCobuttonModelorProfile: palette.black,

    // Sidebar
    sidebar: palette.white,
    textSidebar: palette.grayDark,
    profile: palette.white,
    sidebarItem: 'transparent',
    sidebarItemHover: alpha(palette.petrol, 0.06),
    sidebarItemActive: alpha(palette.petrol, 0.12),

    // Elementos de interface
    buttonMode: palette.petrol,
    tooltip: palette.white,
    tooltipBorder: lightSurface.border,

    // Modais
    modalBackground: alpha(palette.black, 0.55),
    modalBorder: lightSurface.border,
    deleteModalBackground: palette.white,
    deleteModalBorder: lightSurface.border,
    backgroundCancelButtonDeleteModal: 'transparent',
    borderCancelButtonDeleteModal: lightSurface.border,
    hoverCancelButtonDeleteModal: lightSurface.base,
    backgroundConfirmButtonDeleteModal: feedback.errorLight,
    borderConfirmButtonDeleteModal: feedback.errorLight,
    hoverConfirmButtonDeleteModal: '#A32020',

    // Formulários e entradas
    inputLogin: palette.white,
    inputBorder: lightSurface.border,
    usersDivBackground: palette.white,
    formUserBackground: palette.white,
    formUserInput: lightSurface.base,
    formUserBorder: lightSurface.border,
    inputBackgroundAccount: lightSurface.base,
    inputDisabled: lightSurface.base,
    disabledBackground: lightSurface.base,
    borderInputCompliance: lightSurface.border,
    ruleDetailsInputBackground: lightSurface.base,
    ruleDetailsInputBorder: lightSurface.border,
    ruleDetailsInputText: palette.black,
    forgotPasswordBoxBackground: palette.white,

    // Upload
    dropzoneBackground: lightSurface.base,
    dropzoneActiveBackground: alpha(palette.petrol, 0.08),

    // Alertas e estados
    alertBgColor: '#FDECEC',
    alertBorderColor: '#F5C2C2',
    alertIconColor: feedback.errorLight,
    alertTextColor: '#7A1616',
    inputErrorBg: '#FDECEC',
    inputErrorBorder: '#E88A8A',
    inputSuccessBg: '#E8F6F0',
    error: feedback.errorLight,              // 5.9:1 sobre branco
    errorBackground: '#FDECEC',
    success: feedback.successLight,
    successBackground: '#E8F6F0',

    // Resultados e botões
    resultBackground: palette.white,
    buttonBackground: lightSurface.base,
    buttonText: palette.black,
    buttonHoverBackground: lightSurface.border,
    buttonColorProfile: alpha(palette.petrol, 0.1),
    buttonBackAccount: palette.black,
    saveButtonBackground: palette.petrol,
    saveButtonText: palette.white,
    saveButtonHover: petrol.shade15,

    // Código
    codeBackground: lightSurface.base,
    codeText: palette.grayDark,

    /** Cor de identidade enviada ao webwidget. */
    widgetBackground: palette.petrol,


    /*
      PAPÉIS — nomeados pela FUNÇÃO, não pelo elemento.

      O resto deste arquivo nomeia por elemento: `inputAddEditUser`, `ruleDetailsInputBackground`,
      `backgroundCancelButtonDeleteModal`. Isso obriga um token NOVO a cada tela nova, e é por isso
      que existem ~85 deles. Os quatro abaixo são a direção oposta, e o destino da migração: um
      token por papel, como no webwidget.
    */
    /** Base do degradê de fundo; o topo é `background`. Preto Hexium sobre ela: 13.95:1. */
    backgroundDeep: palette.grayLight,
    /** Tinta sobre `primary` (petrol). Branco sobre petrol: 10.45:1. */
    primaryOn: palette.white,
    /** Tinta sobre `error` e `success` — no tema claro os semânticos são escuros. */
    errorOn: palette.white,
    successOn: palette.white,

    /** Aviso — a marca nao especifica; ambar ajustado para passar contraste no tema claro. */
    warning: '#8A5300',
    warningBackground: '#FFF4E0',
    warningOn: palette.white,

    input: {
        background: lightSurface.base,
    },
} as const;
